import React, { useState, useEffect, useMemo, useRef, useId, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    getAllMedications,
    loadMedicationCatalog,
    splitDoseStrengths,
} from '../data/medications';
import Modal, { DialogHeader, DialogFooter, DialogBody } from './ui/Modal';
import DropdownPanel from './ui/DropdownPanel';
import { ChevronDown, Plus } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const SearchableDropdown = ({
    value,
    onChange,
    onSelectItem,
    options,
    getLabel = (o) => o,
    getKey = (o, i) => i,
    placeholder,
    inputId,
    renderOption,
    customLabel = 'Use custom value',
    disabled = false,
    emptyHint = 'No matches',
}) => {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const wrapperRef = useRef(null);
    const listboxId = useId();

    const matches = useMemo(() => {
        const q = (value || '').trim().toLowerCase();
        if (!options || options.length === 0) return [];
        if (!q) return options.slice(0, 100);
        const starts = [];
        const contains = [];
        for (const o of options) {
            const label = String(getLabel(o)).toLowerCase();
            if (label.startsWith(q)) starts.push(o);
            else if (label.includes(q)) contains.push(o);
        }
        return [...starts, ...contains].slice(0, 100);
    }, [value, options, getLabel]);

    const exactMatch = useMemo(() => {
        const q = (value || '').trim().toLowerCase();
        if (!q) return null;
        return options.find((o) => String(getLabel(o)).toLowerCase() === q) || null;
    }, [value, options, getLabel]);

    const showCustomOption = !!(value || '').trim() && !exactMatch;

    useEffect(() => {
        setHighlight(0);
    }, [value, open]);

    useEffect(() => {
        const onDocMouseDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, []);

    const commit = (option) => {
        if (onSelectItem) onSelectItem(option);
        else onChange(getLabel(option));
        setOpen(false);
    };

    const handleKeyDown = (e) => {
        if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setOpen(true);
            return;
        }
        const total = matches.length + (showCustomOption ? 1 : 0);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (total === 0) return;
            setHighlight((h) => (h + 1) % total);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (total === 0) return;
            setHighlight((h) => (h - 1 + total) % total);
        } else if (e.key === 'Enter') {
            if (!open) return;
            e.preventDefault();
            if (highlight < matches.length) {
                commit(matches[highlight]);
            } else if (showCustomOption) {
                setOpen(false);
            }
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const renderHighlighted = (label) => {
        const q = (value || '').trim();
        if (!q) return label;
        const idx = label.toLowerCase().indexOf(q.toLowerCase());
        if (idx === -1) return label;
        return (
            <>
                {label.slice(0, idx)}
                <mark className="bg-teal-100 text-teal-900 rounded px-0.5">
                    {label.slice(idx, idx + q.length)}
                </mark>
                {label.slice(idx + q.length)}
            </>
        );
    };

    return (
        <div ref={wrapperRef} className="relative">
            <div className="relative">
                <input
                    id={inputId}
                    type="text"
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    value={value ?? ''}
                    onChange={(e) => {
                        onChange(e.target.value);
                        if (!open) setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30 disabled:bg-gray-50 disabled:text-gray-400"
                />
                <button
                    type="button"
                    tabIndex={-1}
                    aria-label={open ? 'Close list' : 'Open list'}
                    disabled={disabled}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        if (disabled) return;
                        setOpen((v) => !v);
                    }}
                    className="absolute inset-y-0 right-0 flex w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                >
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={2} aria-hidden="true" />
                </button>
            </div>

            <DropdownPanel
                open={open && !disabled}
                transformOrigin="top center"
                className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/[0.04]"
            >
                <ul
                    id={listboxId}
                    role="listbox"
                    className="max-h-72 overflow-y-auto py-1 text-sm"
                >
                    {matches.length === 0 && !showCustomOption && (
                        <li className="px-3 py-2 text-gray-500">{emptyHint}</li>
                    )}
                    {matches.map((o, i) => {
                        const active = i === highlight;
                        const label = getLabel(o);
                        return (
                            <li
                                key={getKey(o, i)}
                                role="option"
                                aria-selected={active}
                                onMouseEnter={() => setHighlight(i)}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    commit(o);
                                }}
                                className={`cursor-pointer px-3 py-2 ${active ? 'bg-teal-50' : 'hover:bg-gray-50'}`}
                            >
                                {renderOption ? renderOption(o, { highlighted: active, query: value, renderHighlighted }) : (
                                    <span className="text-gray-900">{renderHighlighted(label)}</span>
                                )}
                            </li>
                        );
                    })}
                    {showCustomOption && (
                        <li
                            role="option"
                            aria-selected={highlight === matches.length}
                            onMouseEnter={() => setHighlight(matches.length)}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setOpen(false);
                            }}
                            className={`flex cursor-pointer items-center gap-2 border-t border-gray-100 px-3 py-2 text-gray-700 ${
                                highlight === matches.length ? 'bg-teal-50' : 'hover:bg-gray-50'
                            }`}
                        >
                            <Plus className="h-4 w-4 text-teal-600" strokeWidth={2} aria-hidden="true" />
                            <span className="text-sm">
                                {customLabel} <span className="font-semibold text-gray-900">“{value.trim()}”</span>
                            </span>
                        </li>
                    )}
                </ul>
            </DropdownPanel>
        </div>
    );
};

const MedicationManager = ({ patientMRN, catalogVersion = 0 }) => {
    const { user } = useAuth();
    const [patientMedications, setPatientMedications] = useState([]);
    const [medHistory, setMedHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [isMedModalOpen, setIsMedModalOpen] = useState(false);
    const [isEditingMed, setIsEditingMed] = useState(false);
    const [medFormData, setMedFormData] = useState({
        id: null,
        drugName: '',
        brandName: '',
        dosage: '',
        frequency: '',
        startDate: '',
        recommendation: '',
    });
    const [selectedDrugMeta, setSelectedDrugMeta] = useState(null);
    const [, setCatalogReadyTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        loadMedicationCatalog().then(() => {
            if (!cancelled) setCatalogReadyTick((t) => t + 1);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const medsCatalog = getAllMedications();

    const findCatalogEntry = (name) => {
        if (!name) return null;
        const q = name.trim().toLowerCase();
        return medsCatalog.find((m) => m.drugName.toLowerCase() === q) || null;
    };

    const fetchPatientMedications = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const res = await fetch(`${API_BASE_URL}/medications/patient/${patientMRN}/active`);
            if (!res.ok) throw new Error('Failed to fetch medications');

            const data = await res.json();

            setPatientMedications(data.current || []);
            setMedHistory(data.history || []);

            setLoading(false);

        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [patientMRN]);

    useEffect(() => {
        if (patientMRN && user?.user_id) {
            fetchPatientMedications();
        }
    }, [patientMRN, user?.user_id, fetchPatientMedications]);

    const handleAddMedication = () => {
        setMedFormData({
            id: null,
            drugName: '',
            brandName: '',
            dosage: '',
            frequency: '',
            frequency_code: '',
            startDate: '',
            recommendation: '',
        });
        setSelectedDrugMeta(null);
        setIsEditingMed(false);
        setIsMedModalOpen(true);
    };

    const handleEditMedication = (med) => {
        setMedFormData({
            id: med.id,
            drugName: med.drugName || '',
            brandName: med.brandName || '',
            dosage: med.dosage || '',
            frequency: med.frequency || '',
            frequency_code: med.frequency_code || '',
            startDate: med.startDate ? med.startDate.split('T')[0] : '',
            recommendation: med.recommendation || '',
        });
        setSelectedDrugMeta(findCatalogEntry(med.drugName));
        setIsEditingMed(true);
        setIsMedModalOpen(true);
    };

    const closeMedicationModal = () => {
        setIsMedModalOpen(false);
    };

    useEffect(() => {
        if (!isMedModalOpen) return undefined;
        const onEsc = (e) => {
            if (e.key === 'Escape') setIsMedModalOpen(false);
        };
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [isMedModalOpen]);

    const handleDrugNameChange = (newName) => {
        setMedFormData((prev) => ({ ...prev, drugName: newName }));
        const matched = findCatalogEntry(newName);
        setSelectedDrugMeta(matched);
    };

    const handleDrugSelect = (entry) => {
        setMedFormData((prev) => ({
            ...prev,
            drugName: entry.drugName,
            dosage: '',
            frequency: '',
            frequency_code: '',
        }));
        setSelectedDrugMeta(entry);
    };

    const doseOptions = useMemo(
        () => splitDoseStrengths(selectedDrugMeta?.commonDoseStrengths),
        [selectedDrugMeta]
    );
    const FREQUENCY_OPTIONS = [
        { code: 'once_daily', label: 'Once Daily' },
        { code: 'twice_daily', label: 'Twice Daily' },
        { code: 'thrice_daily', label: 'Thrice Daily' },
    ];

    const handleSubmitMedication = async () => {
        if (!patientMRN) {
            alert('Error: Patient MRN is missing. Cannot save medication.');
            console.error('patientMRN is undefined or null:', patientMRN);
            return;
        }

        if (!medFormData.drugName || !medFormData.dosage || !medFormData.frequency_code || !medFormData.startDate) {
            alert('Please fill in all required fields: Drug Name, Dosage, Frequency, and Start Date');
            return;
        }

        const payload = {
            drugName: medFormData.drugName,
            brandName: medFormData.brandName,
            dosage: medFormData.dosage,
            frequency: medFormData.frequency,
            frequency_code: medFormData.frequency_code,
            startDate: medFormData.startDate,
            PMRN: parseInt(patientMRN),
        };

        if (medFormData.recommendation) {
            payload.indication = medFormData.recommendation;
        }

        console.log('Submitting medication with payload:', payload);

        try {
            let response;

            if (isEditingMed) {
                response = await fetch(
                    `${API_BASE_URL}/medications/${medFormData.id}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json();
                    const message = errorData.detail || 'Failed to update medication';
                    if (response.status === 403 && /contact admin/i.test(message)) {
                        throw new Error('Unable to proceed, contact admin.');
                    }
                    throw new Error(message);
                }
            } else {
                response = await fetch(`${API_BASE_URL}/medications/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    const message = errorData.detail || 'Failed to add medication';
                    if (response.status === 403 && /contact admin/i.test(message)) {
                        throw new Error('Unable to proceed, contact admin.');
                    }
                    throw new Error(message);
                }
            }

            await fetchPatientMedications();
            setIsMedModalOpen(false);
            setSuccessMessage(isEditingMed ? 'Medication updated successfully!' : 'Medication added successfully!');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Error saving medication:', err);
            alert('Error saving medication: ' + err.message);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
            <p className="text-gray-600 font-medium">Loading patient medications...</p>
        </div>
    );
    if (error) return <p className="text-red-600">Error: {error}</p>;

    if (!patientMRN) {
        return <p className="text-red-600">Error: Patient MRN is not provided to this component</p>;
    }

    return (
        <div>
            
            {successMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    {successMessage}
                </div>
            )}

            
            <div className="bg-white p-6 rounded-md shadow-sm border mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">Current Medications</h3>
                    <button
                        className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-500 text-sm"
                        onClick={handleAddMedication}
                    >
                        Add New Medication
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Name', 'Brand', 'Dose', 'Frequency', 'Start Date', 'Assigned To', 'Action', 'Recommendation'].map((head) => (
                                    <th key={head} className="px-4 py-3 text-left font-semibold text-gray-600">{head}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {patientMedications.length > 0 ? (
                                patientMedications.map((med, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-2">{med.drugName}</td>
                                        <td className="px-4 py-2">{med.brandName}</td>
                                        <td className="px-4 py-2">{med.dosage}</td>
                                        <td className="px-4 py-2">{med.frequency}</td>
                                        <td className="px-4 py-2">{med.startDate ? med.startDate.split('T')[0] : ''}</td>
                                        <td className="px-4 py-2">{med.assignedTo}</td>
                                        <td className="px-4 py-2">
                                            <button
                                                className="text-blue-500 hover:underline"
                                                onClick={() => handleEditMedication(med)}
                                            >
                                                Adjust
                                            </button>
                                        </td>
                                        <td className="px-4 py-2 text-sm">{med.recommendation}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center py-4 text-gray-500">
                                        No medications found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            
            <div className="bg-white p-6 rounded-md shadow-sm border">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Medications Change History</h3>
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {['Date', 'Old Dose', 'New Dose', 'Old Freq.', 'New Freq.', 'Old Name'].map((head) => (
                                <th key={head} className="px-4 py-3 text-left font-semibold text-gray-600">{head}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {medHistory.length > 0 ? (
                            medHistory.map((historyItem, idx) => (
                                <tr key={idx}>
                                    <td className="px-4 py-2">{historyItem.changedAt ? new Date(historyItem.changedAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</td>
                                    <td className="px-4 py-2">{historyItem.oldDosage || '-'}</td>
                                    <td className="px-4 py-2">{historyItem.newDosage || '-'}</td>
                                    <td className="px-4 py-2">{historyItem.oldFrequency || '-'}</td>
                                    <td className="px-4 py-2">{historyItem.newFrequency || '-'}</td>
                                    <td className="px-4 py-2">{historyItem.olddrugName || '-'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center py-4 text-gray-500">No medication history found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

           
            <Modal
                open={isMedModalOpen}
                onClose={closeMedicationModal}
                align="start"
            >
                    <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[calc(100vh-6rem)] sm:max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col border border-gray-100">
                        <DialogHeader
                            title={isEditingMed ? 'Edit Medication' : 'Add Medication'}
                            subtitle="Search the catalog or enter custom values."
                            onClose={closeMedicationModal}
                            tone="muted"
                        />

                        <DialogBody className="px-6 py-6 space-y-5">
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Drug Name <span className="text-red-500">*</span>
                                </label>
                                <SearchableDropdown
                                    value={medFormData.drugName}
                                    onChange={handleDrugNameChange}
                                    onSelectItem={handleDrugSelect}
                                    options={medsCatalog}
                                    getLabel={(o) => o.drugName}
                                    getKey={(o) => o.drugName}
                                    placeholder="Search e.g., Metformin"
                                    customLabel="Use custom drug name"
                                    emptyHint="No matching medications"
                                    renderOption={(o, { renderHighlighted }) => (
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="truncate font-medium text-gray-900">
                                                    {renderHighlighted(o.drugName)}
                                                    {o.isCustom && (
                                                        <span className="ml-2 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 border border-amber-200">
                                                            Custom
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="truncate text-xs text-gray-500">
                                                    {[o.drugClass, o.subCategory].filter(Boolean).join(' • ') || '—'}
                                                </div>
                                            </div>
                                            {o.route && (
                                                <span className="shrink-0 self-center rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700">
                                                    {o.route}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                />
                                
                                {selectedDrugMeta && (
                                    <div className="mt-2 rounded-md bg-gray-50 border border-gray-100 px-3 py-2 text-[11px] text-gray-500 leading-relaxed">
                                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                                            <span><span className="font-medium text-gray-600">Category:</span> {selectedDrugMeta.category || '—'}</span>
                                            <span><span className="font-medium text-gray-600">Sub-category:</span> {selectedDrugMeta.subCategory || '—'}</span>
                                            <span><span className="font-medium text-gray-600">Class:</span> {selectedDrugMeta.drugClass || '—'}</span>
                                            <span><span className="font-medium text-gray-600">Usual Route:</span> {selectedDrugMeta.route || '—'}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Brand Name
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g., Glucophage"
                                    value={medFormData.brandName}
                                    onChange={(e) => setMedFormData({ ...medFormData, brandName: e.target.value })}
                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                />
                            </div>

                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Dosage <span className="text-red-500">*</span>
                                    </label>
                                    <SearchableDropdown
                                        value={medFormData.dosage}
                                        onChange={(v) => setMedFormData((p) => ({ ...p, dosage: v }))}
                                        options={doseOptions}
                                        getLabel={(o) => o}
                                        getKey={(o, i) => `${o}-${i}`}
                                        placeholder={selectedDrugMeta ? 'Select or type dose' : 'e.g., 500 mg'}
                                        customLabel="Use custom dose"
                                        emptyHint={selectedDrugMeta ? 'No common doses listed' : 'Pick a drug for suggestions'}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Frequency <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={medFormData.frequency_code || ''}
                                        onChange={(e) => {
                                            const code = e.target.value;
                                            const match = FREQUENCY_OPTIONS.find((o) => o.code === code);
                                            setMedFormData((p) => ({
                                                ...p,
                                                frequency_code: code,
                                                frequency: match ? match.label : '',
                                            }));
                                        }}
                                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                        required
                                    >
                                        <option value="">Select frequency</option>
                                        {FREQUENCY_OPTIONS.map((opt) => (
                                            <option key={opt.code} value={opt.code}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Start Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={medFormData.startDate}
                                    onChange={(e) => setMedFormData({ ...medFormData, startDate: e.target.value })}
                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                    required
                                />
                            </div>

                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Recommendation / Notes
                                </label>
                                <textarea
                                    placeholder="e.g., Take with food, Monitor blood sugar levels"
                                    value={medFormData.recommendation}
                                    onChange={(e) => setMedFormData({ ...medFormData, recommendation: e.target.value })}
                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                    rows={3}
                                />
                            </div>
                        </DialogBody>

                        <DialogFooter>
                            <button
                                type="button"
                                onClick={closeMedicationModal}
                                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-sm hover:bg-white transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmitMedication}
                                className="px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition shadow-sm"
                            >
                                {isEditingMed ? 'Update Medication' : 'Add Medication'}
                            </button>
                        </DialogFooter>
                    </div>
            </Modal>
        </div>
    );
};

export default MedicationManager;
